export type ServiceRecordStatus =
  | 'RECEIVED'
  | 'DIAGNOSIS'
  | 'WAITING_FOR_APPROVAL'
  | 'IN_REPAIR'
  | 'READY'
  | 'HANDED_OVER';

export type ServiceRecordStatusFilter = ServiceRecordStatus | 'ACTIVE' | 'ALL';

export const ACTIVE_STATUSES: ServiceRecordStatus[] = [
  'RECEIVED',
  'DIAGNOSIS',
  'WAITING_FOR_APPROVAL',
  'IN_REPAIR',
  'READY',
];

export type PhotoType = 'INTAKE' | 'REPAIR' | 'RESULT';

export interface ServicePhoto {
  id: string;
  recordUuid: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  photoType: PhotoType;
  isPrimary: boolean;
  width: number | null;
  height: number | null;
  createdAt: number;
  updatedAt?: number;
  isDeleted?: boolean;
  deletedAt?: number | null;
  revision: number;
  idempotencyKey?: string | null;
}

export interface ServiceRecord {
  uuid: string;
  ticketNumber: string;
  revision: number;
  createdAt: number;
  serverUpdatedAt: number;
  clientUpdatedAt: number;
  clientName: string;
  clientPhone: string;
  clientPhoneNormalized?: string;
  deviceType: string;
  brand: string;
  model: string;
  serialNumber?: string;
  description?: string;
  visualCondition?: string;
  status: ServiceRecordStatus;
  priceCents?: number | null;
  notes?: string;
  completedAt?: number | null;
  handedOverAt?: number | null;
  isDeleted?: boolean;
  deletedAt?: number | null;
}

export interface ServiceRecordPatchDto {
  expectedRevision: number;
  clientName?: string;
  clientPhone?: string;
  deviceType?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  description?: string;
  visualCondition?: string;
  status?: ServiceRecordStatus;
  priceCents?: number | null;
  notes?: string;
}

export interface ApiPagination {
  total: number;
  limit: number;
  offset: number;
}

export interface ApiListResponse {
  data: ServiceRecord[];
  pagination: ApiPagination;
}

export interface ApiSingleResponse {
  data: ServiceRecord;
}

export interface ApiError {
  code: string;
  message: string;
  requestId?: string;
}

export interface ApiErrorResponse {
  error: ApiError;
}

export interface StatusOption {
  value: ServiceRecordStatus | 'ALL';
  label: string;
  color: string;
  bgColor: string;
}

export const VALID_SERVICE_STATUSES: ServiceRecordStatus[] = [
  'RECEIVED',
  'DIAGNOSIS',
  'WAITING_FOR_APPROVAL',
  'IN_REPAIR',
  'READY',
  'HANDED_OVER',
];

export const STATUS_MAP: Record<ServiceRecordStatus, { label: string; color: string; bgColor: string }> = {
  RECEIVED: { label: 'Przyjęte', color: '#2563eb', bgColor: '#dbeafe' },
  DIAGNOSIS: { label: 'Diagnoza', color: '#7c3aed', bgColor: '#ede9fe' },
  WAITING_FOR_APPROVAL: { label: 'Oczekuje na akceptację', color: '#d97706', bgColor: '#fef3c7' },
  IN_REPAIR: { label: 'W naprawie', color: '#0284c7', bgColor: '#e0f2fe' },
  READY: { label: 'Gotowe do odbioru', color: '#059669', bgColor: '#d1fae5' },
  HANDED_OVER: { label: 'Wydane', color: '#4b5563', bgColor: '#f3f4f6' },
};

export type CommunicationType =
  | 'RECEIVED_CONFIRMATION'
  | 'REPAIR_UPDATE'
  | 'READY_FOR_PICKUP'
  | 'CUSTOM'
  | 'SERVICE_INTAKE'
  | 'MANUAL_NOTE'
  | 'STATUS_CHANGE';

export type CommunicationChannel =
  | 'CLIPBOARD'
  | 'SMS_APP'
  | 'INTERNAL'
  | 'NONE'
  | 'INTERNAL_NOTE'
  | 'PHONE_CALL'
  | 'CUSTOMER_AGREEMENT';

export type CommunicationAction =
  | 'COPIED_FOR_SENDING'
  | 'HANDED_TO_SMS_APP'
  | 'SYSTEM_EVENT'
  | 'MANUAL_ENTRY';

export type CommunicationSource = 'WEB' | 'ANDROID';

export interface RecordCommunication {
  uuid: string;
  recordUuid: string;
  type: CommunicationType;
  channel: CommunicationChannel;
  action: CommunicationAction;
  source: CommunicationSource;
  content: string;
  createdAt: string;
  actor: string | null;
  revision: number;
  idempotencyKey?: string | null;
  isDeleted?: boolean;
  deletedAt?: string | null;
  deletedBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface ServiceRecordCreateDto {
  clientName?: string;
  clientPhone: string;
  deviceType?: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  description?: string;
  visualCondition?: string;
  status?: ServiceRecordStatus;
  priceCents?: number | null;
  notes?: string;
  idempotencyKey?: string;
}

